import { useChronicBillingLines } from "../../../hooks/useChronicBillingLines";
import { CHRONIC_REASON_OPTIONS, isChronicBillingEventBilled, isChronicProductInfoBilled, parseChronicDescription, resolveChronicMedicamentId } from "../../../utils/chronicBilling";
import { useState } from "react";
import { Modal } from "./AdministrationFields";
import { ADMIN_API_CONFIG } from "../model/administrationConstants";
import { formatDate, formatDateTime } from "../model/administrationRules";
import {
    chronicAddedMetaStyle,
    chronicBillingActionsStyle,
    chronicBillingCellStyle,
    chronicBillingEmptyStyle,
    chronicBillingHeaderStyle,
    chronicBillingProductStyle,
    chronicBillingRowStyle,
    chronicBillingRowsStyle,
    chronicDirectReasonFieldStyle,
    chronicHistoryButtonStyle,
    chronicHistoryProductInfoStyle,
    chronicInlineErrorStyle,
    chronicMarkBilledButtonStyle,
    chronicProductCommentFieldStyle,
    chronicProductCommentTextareaStyle,
    chronicRemoveHistoryButtonStyle,
    chronicRowActionsStyle,
    dangerButtonStyle,
    primaryButtonStyle,
    secondaryButtonStyle,
} from "../styles/administrationModalStyles";
import { inputStyle } from "../styles/administrationStyles";

export function ChronicBillingCell({ row, user, onOpenProducts, onOpenHistory, onChanged }) {
    const [hoveredId, setHoveredId] = useState(null);
    const [error, setError] = useState("");
    const lines = useChronicBillingLines({
        user,
        reload: onChanged,
        apiConfig: ADMIN_API_CONFIG,
        resolveMedicamentId: resolveChronicMedicamentId,
        onError: setError,
    });
    const { savingId, confirmBill: confirmMarkBilled, confirmDelete: confirmRemoveFromHistory } = lines;
    const confirmEvent = lines.billCandidate?.event || null;
    const setConfirmEvent = event => lines.setBillCandidate(event ? { patient: row, event } : null);
    const removeHistoryEvent = lines.deleteCandidate?.event || null;
    const setRemoveHistoryEvent = event => lines.setDeleteCandidate(event ? { patient: row, event } : null);
    const updateReason = (billingEvent, reason) => lines.updateReason(row, billingEvent, reason);
    const updateComment = (billingEvent, comment) => lines.updateComment(row, billingEvent, comment);
    const events = (row.billingEvents || [])
        .filter(event => event.type === "CHRONIC")
        .filter(event => !isChronicBillingEventBilled(event));

    return (
        <div style={chronicBillingCellStyle}>
            <div style={chronicBillingHeaderStyle}>
                <button type="button" onClick={() => onOpenHistory(row)} style={chronicHistoryButtonStyle}>
                    Voir historique
                </button>
            </div>
            {events.length === 0 ? (
                <span style={chronicBillingEmptyStyle}>Aucun produit ajouté.</span>
            ) : (
                <div style={chronicBillingRowsStyle}>
                    {events.map(event => {
                        const info = parseChronicDescription(event.description);
                        const canMarkBilled = !isChronicProductInfoBilled(info.productInfo);
                        return (
                            <div
                                key={event.id}
                                style={chronicBillingRowStyle(hoveredId === event.id)}
                                onMouseEnter={() => setHoveredId(event.id)}
                                onMouseLeave={() => setHoveredId(null)}
                            >
                                <div style={chronicBillingProductStyle}>
                                    <strong>{info.productInfo || "Produit sans information"}</strong>
                                    <small>Date produit : {formatDate(event.date)}</small>
                                    <small style={chronicAddedMetaStyle}>
                                        Mis en facturation le {formatDateTime(event.createdAt) || "date inconnue"} par {event.createdBy || "utilisateur inconnu"}
                                    </small>
                                </div>
                                <label style={chronicDirectReasonFieldStyle}>
                                    <span>Motif</span>
                                    <select
                                        value={info.reason}
                                        onChange={selectEvent => updateReason(event, selectEvent.target.value)}
                                        disabled={savingId === event.id}
                                        style={inputStyle}
                                    >
                                        <option value="">—</option>
                                        {CHRONIC_REASON_OPTIONS.filter(Boolean).map(reason => (
                                            <option key={reason} value={reason}>{reason}</option>
                                        ))}
                                    </select>
                                </label>
                                <label style={chronicProductCommentFieldStyle}>
                                    <span>Commentaire</span>
                                    <textarea
                                        defaultValue={info.comment}
                                        onBlur={blurEvent => updateComment(event, blurEvent.target.value)}
                                        disabled={savingId === event.id}
                                        placeholder="Commentaire produit"
                                        style={chronicProductCommentTextareaStyle}
                                    />
                                </label>
                                <div style={chronicRowActionsStyle}>
                                    {canMarkBilled && (
                                        <button
                                            type="button"
                                            onClick={() => setConfirmEvent(event)}
                                            disabled={savingId === event.id}
                                            style={chronicMarkBilledButtonStyle}
                                        >
                                            Marquer facturé
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setRemoveHistoryEvent(event)}
                                        disabled={savingId === event.id}
                                        style={chronicRemoveHistoryButtonStyle}
                                    >
                                        Retirer de la facturation
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <div style={chronicBillingActionsStyle}>
                <button type="button" onClick={() => onOpenProducts({ ...row, __manualChronicProduct: true })} style={secondaryButtonStyle}>
                    Ajouter un produit
                </button>
            </div>
            {error && <span style={chronicInlineErrorStyle}>{error}</span>}
            {confirmEvent && (
                <Modal title="Marquer comme facturé ?" onClose={() => setConfirmEvent(null)} width={500}>
                    <div style={{ display: "grid", gap: "18px" }}>
                        <p style={{ margin: 0, color: "#374151", lineHeight: 1.6 }}>
                            Confirmer que vous voulez marquer ce produit comme facturé ? La date et l'opérateur seront enregistrés automatiquement.
                        </p>
                        <div style={chronicHistoryProductInfoStyle}>
                            {parseChronicDescription(confirmEvent.description).productInfo || "Produit sans information"}
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                            <button type="button" onClick={() => setConfirmEvent(null)} disabled={savingId === confirmEvent.id} style={secondaryButtonStyle}>
                                Annuler
                            </button>
                            <button type="button" onClick={confirmMarkBilled} disabled={savingId === confirmEvent.id} style={primaryButtonStyle}>
                                {savingId === confirmEvent.id ? "Enregistrement…" : "Valider"}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
            {removeHistoryEvent && (
                <Modal title="Retirer de la facturation ?" onClose={() => setRemoveHistoryEvent(null)} width={520}>
                    <div style={{ display: "grid", gap: "18px" }}>
                        <p style={{ margin: 0, color: "#374151", lineHeight: 1.6 }}>
                            Confirmer que vous voulez retirer ce produit de la facturation chronique ?
                        </p>
                        <div style={chronicHistoryProductInfoStyle}>
                            {parseChronicDescription(removeHistoryEvent.description).productInfo || "Produit sans information"}
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", flexWrap: "wrap" }}>
                            <button type="button" onClick={() => setRemoveHistoryEvent(null)} disabled={savingId === removeHistoryEvent.id} style={secondaryButtonStyle}>
                                Annuler
                            </button>
                            <button type="button" onClick={confirmRemoveFromHistory} disabled={savingId === removeHistoryEvent.id} style={dangerButtonStyle}>
                                {savingId === removeHistoryEvent.id ? "Suppression..." : "Retirer de la facturation"}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}
